import { useCallback, useEffect, useRef, useState } from 'react';
import type { RaceOverview, SessionProcess } from '../../shared/overview';
import { errorMessage, request } from '../api';
import { matchesProcess, mergeOverviewProcess, processIdentity } from './overviewProcess';

export function useOverview() {
  const [overview, setOverview] = useState<RaceOverview | null>(null);
  const [loading, setLoading] = useState(true), [error, setError] = useState<string | null>(null);
  const [processFailure, setProcessFailure] = useState<{ identity: string; message: string } | null>(null);
  const [processRetry, setProcessRetry] = useState(0);
  const anchor = useRef({ server: Date.now(), client: performance.now() });
  const controller = useRef<AbortController | null>(null);
  // Only the current loaded process is retained; no unbounded browser history cache.
  const processCache = useRef<SessionProcess | null>(null);
  const refreshSummary = useCallback(async () => {
    if (controller.current) return;
    const current = new AbortController(); controller.current = current;
    try {
      const value = await request<RaceOverview>('/api/overview?view=summary', AbortSignal.any([current.signal, AbortSignal.timeout(25_000)]));
      if (current.signal.aborted) return;
      if (!Number.isFinite(Date.parse(value.serverTime))) throw new Error('赛事摘要时间无效');
      anchor.current = { server: Date.parse(value.serverTime), client: performance.now() };
      setOverview(mergeOverviewProcess(value, processCache.current)); setError(null);
    } catch (err) { if (!current.signal.aborted) setError(errorMessage(err)); }
    finally { if (controller.current === current) { controller.current = null; setLoading(false); } }
  }, []);
  const retryProcess = useCallback(() => setProcessRetry(value => value + 1), []);
  const refresh = useCallback(async () => { retryProcess(); await refreshSummary(); }, [refreshSummary, retryProcess]);
  useEffect(() => {
    void refreshSummary(); const timer = setInterval(() => void refreshSummary(), 30_000);
    return () => { clearInterval(timer); controller.current?.abort(); controller.current = null; };
  }, [refreshSummary]);

  const identity = processIdentity(overview?.result);
  useEffect(() => {
    const result = overview?.result;
    if (!identity || !result?.process) { processCache.current = null; return; }
    if (matchesProcess(result, processCache.current)) return;
    processCache.current = null;
    const current = new AbortController();
    setProcessFailure(null);
    const query = new URLSearchParams({ view: 'process', sessionKey: result.session.key, version: result.process.version });
    void request<SessionProcess>(`/api/overview?${query}`, AbortSignal.any([current.signal, AbortSignal.timeout(150_000)]))
      .then(value => {
        if (current.signal.aborted) return;
        if (!matchesProcess(result, value)) throw new Error('比赛过程版本已变化，正在刷新赛事摘要。');
        processCache.current = value;
        setOverview(latest => latest ? mergeOverviewProcess(latest, value) : latest);
      })
      .catch(err => {
        if (current.signal.aborted) return;
        setProcessFailure({ identity, message: errorMessage(err) });
        // A 409 means a new revision exists. Refresh once; unchanged revisions do not redownload on every poll.
        void refreshSummary();
      });
    return () => current.abort();
    // The content identity, rather than every summary poll/fetchedAt change, owns the download lifecycle.
  }, [identity, processRetry, refreshSummary]);

  const processError = identity && processFailure?.identity === identity ? processFailure.message : null;
  const processState = overview?.result?.events.length ? 'ready' : !identity ? 'none' : processError ? 'error' : 'loading';
  const processNotice = processState === 'loading' ? '成绩已就绪，比赛过程正在后台加载…'
    : processState === 'error' ? `比赛过程暂未加载：${processError} 成绩仍可查看，可刷新重试。` : null;
  return { overview, loading, error, refresh, processState, processError, processNotice, retryProcess,
    serverNow: () => anchor.current.server + performance.now() - anchor.current.client };
}
